import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingPage } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ConfirmModal } from '../../components/common/Modal';
import { KillSwitchToggle } from '../../components/bot-admin/KillSwitchToggle';
import { ChannelClaudeTokenField } from '../../components/bot-admin/ChannelClaudeTokenField';
import { ChannelProjectLinks } from '../../components/bot-admin/ChannelProjectLinks';
import { ProjectQueueConfigForm } from '../../components/bot-admin/ProjectQueueConfigForm';
import { RoutingOverridePanel } from '../../components/bot-admin/RoutingOverridePanel';
import { UsageTab } from '../../components/bot-admin/UsageTab';
import { PaperclipMembersTab } from '../../components/bot-admin/PaperclipMembersTab';
import { useBotConnections, useSetConnectionDisabled } from '../../hooks/useBotAdmin';
import { formatDisplayDateTime } from '../../utils/format';
import type { BotAdminConnection } from '../../services/api/botAdmin';

/**
 * Phase 75 (Plan 75-05) — the single bot-admin page (D-07): connected-users
 * table (ADMIN-01, columns per D-09), an org kill-switch toggle at the top
 * (ADMIN-02), and inline per-user disable/enable (ADMIN-05). Structural
 * clone of `AppAccessMatrixPage.tsx` (LoadingPage while fetching, empty
 * state, table shell) — server-confirm-only mutations, no optimistic
 * update, matching `useBotAdmin.ts`'s pattern.
 *
 * Phase 87 (Plan 87-08 / D-07) — retrofit to the tab pattern established by
 * `AdminTemplatesPage.tsx` (in-memory `useState<Tab>`, no URL-param syncing
 * per RESEARCH Open Question 3): a new "Usage" tab renders `UsageTab`
 * alongside the pre-existing "Connections" tab, which carries every
 * pre-87-08 section unchanged. No new nav item is added.
 *
 * Phase 121 (Plan 121-03 / PCATTR-06, PCATTR-07) — a third "Factory™" tab
 * (`?tab=paperclip`) renders `PaperclipMembersTab`: the per-person attribution
 * switch, per-company roster refresh and the people table with its guarded
 * Enable action.
 */
type Tab = 'connections' | 'usage' | 'paperclip';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'connections', label: 'Connections' },
  { id: 'usage', label: 'Usage' },
  { id: 'paperclip', label: 'Factory™' },
];

function isTab(value: string | null): value is Tab {
  return TABS.some((t) => t.id === value);
}

export default function BotAdminPage() {
  const navigate = useNavigate();
  // Tab lives in the URL (2026-09-08) so the bot-usage notification can
  // deep-link to /admin/bot?tab=usage. An unknown or absent value falls back
  // to Connections, which is the pre-existing default — a bad ?tab= must not
  // render a blank page.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'connections';
  const setTab = (next: Tab): void => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    // replace: switching tabs should not stack history entries the way a
    // navigation would.
    setSearchParams(params, { replace: true });
  };
  const { data: connections, isLoading, error, refetch } = useBotConnections();
  const setDisabledMutation = useSetConnectionDisabled();

  // Lightweight inline confirm for the per-row disable/enable button — disable
  // is reversible and lower-stakes than the kill switch (T-75-05-02), so a
  // full typed-confirm isn't required, but we still never use window.confirm().
  const [pendingRowAction, setPendingRowAction] = useState<BotAdminConnection | null>(null);

  // T-87-08-04: the connections-query LoadingPage guard now only blocks the
  // Connections tab's own render branch below (not the whole page) so a slow
  // connections query never blocks the Usage tab from rendering.

  const rows = connections ?? [];

  const requestToggleDisabled = (row: BotAdminConnection) => {
    setPendingRowAction(row);
  };

  const closeRowModal = () => {
    if (setDisabledMutation.isPending) return;
    setPendingRowAction(null);
  };

  const confirmToggleDisabled = () => {
    if (!pendingRowAction) return;
    setDisabledMutation.mutate(
      { connectionId: pendingRowAction.connectionId, disabled: !pendingRowAction.disabledByAdmin },
      { onSuccess: () => setPendingRowAction(null) }
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Monk Bot Admin</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage Slack bot connections and the org-wide kill switch.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'connections' && (
        <>
          {isLoading ? (
            <LoadingPage />
          ) : (
            <>
              <KillSwitchToggle />

              <ChannelClaudeTokenField />

              <ChannelProjectLinks />

              <ProjectQueueConfigForm />

              <RoutingOverridePanel />

              {error ? (
                <div className="py-12 text-center">
                  <p className="text-sm font-semibold text-red-600">
                    Failed to load bot connections.
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{(error as Error).message}</p>
                  <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
                    Try again
                  </Button>
                </div>
              ) : rows.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-sm text-gray-500">No bot connections yet.</p>
                </div>
              ) : (
                <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          Slack user
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          Monksheets user
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          PAT status
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase"
                        >
                          Claude token
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          Model
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          Last active
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          Connected since
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                        >
                          Disabled
                        </th>
                        <th scope="col" className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rows.map((row, idx) => (
                        <tr
                          key={row.connectionId}
                          className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
                        >
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {row.slackUserId}
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900">
                                {row.displayName ?? '-'}
                              </span>
                              <span className="text-xs text-gray-400">{row.email ?? '-'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {row.patStatus ?? 'unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center text-sm">
                            {row.claudeTokenPresent ? (
                              <span aria-label="Claude token present" className="text-green-600">
                                ✓
                              </span>
                            ) : (
                              <span aria-label="Claude token absent" className="text-gray-300">
                                —
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {row.model ?? 'default'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {formatDisplayDateTime(row.patLastUsedAt)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                            {formatDisplayDateTime(row.connectedAt)}
                          </td>
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            {row.disabledByAdmin ? (
                              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                Disabled
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <Button
                              variant={row.disabledByAdmin ? 'secondary' : 'danger'}
                              size="sm"
                              onClick={() => requestToggleDisabled(row)}
                              isLoading={
                                setDisabledMutation.isPending &&
                                setDisabledMutation.variables?.connectionId === row.connectionId
                              }
                            >
                              {row.disabledByAdmin ? 'Enable' : 'Disable'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pendingRowAction && (
                <ConfirmModal
                  isOpen={true}
                  onClose={closeRowModal}
                  onConfirm={confirmToggleDisabled}
                  title={
                    pendingRowAction.disabledByAdmin
                      ? `Re-enable ${pendingRowAction.displayName ?? pendingRowAction.slackUserId}?`
                      : `Disable ${pendingRowAction.displayName ?? pendingRowAction.slackUserId}?`
                  }
                  message={
                    pendingRowAction.disabledByAdmin
                      ? 'This restores Monk bot access and MCP tool access for this user.'
                      : 'This blocks Monk bot access and MCP tool access for this user immediately. You can re-enable it any time.'
                  }
                  confirmText={pendingRowAction.disabledByAdmin ? 'Re-enable' : 'Disable'}
                  cancelText="Cancel"
                  variant={pendingRowAction.disabledByAdmin ? 'primary' : 'danger'}
                  isLoading={setDisabledMutation.isPending}
                  error={
                    setDisabledMutation.isError
                      ? (setDisabledMutation.error as Error).message
                      : null
                  }
                />
              )}
            </>
          )}
        </>
      )}

      {tab === 'usage' && <UsageTab />}

      {tab === 'paperclip' && <PaperclipMembersTab />}
    </div>
  );
}
